/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddDataChecksToLibraryPathParameters,
  DeleteLibraryPathParameters,
  GetLibraryDataChecksPathParameters,
  GetLibraryPathParameters,
  RemoveDataChecksFromLibraryPathParameters,
  TLDataCheckCreateRequest,
  TLDataChecksCreateResult,
  TLDataChecksRemoveRequest,
  TLDataChecksRemoveResult,
  TLDataChecksResponse,
  TLHTTPValidationError,
  TLLibrariesResponse,
  TLLibraryCreateRequest,
  TLLibraryResponse,
  TLLibraryUpdateRequest,
  TLSuccessResponse,
  UpdateLibraryPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Libraries
 */
export const getAllLibraries = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLLibrariesResponse>({ url: `/v2/data-harmonization/libraries`, method: 'GET', signal }, options);
};

export const getGetAllLibrariesQueryKey = () => {
  return ['v2', 'data-harmonization', 'libraries'] as const;
};

export const getGetAllLibrariesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllLibraries>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllLibrariesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllLibraries>>> = ({ signal }) =>
    getAllLibraries(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllLibraries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllLibrariesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllLibraries>>>;
export type GetAllLibrariesQueryError = ErrorType<unknown>;

export function useGetAllLibraries<TData = Awaited<ReturnType<typeof getAllLibraries>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllLibraries>>,
          TError,
          Awaited<ReturnType<typeof getAllLibraries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllLibraries<TData = Awaited<ReturnType<typeof getAllLibraries>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllLibraries>>,
          TError,
          Awaited<ReturnType<typeof getAllLibraries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllLibraries<TData = Awaited<ReturnType<typeof getAllLibraries>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Libraries
 */

export function useGetAllLibraries<TData = Awaited<ReturnType<typeof getAllLibraries>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllLibrariesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Libraries
 */
export const prefetchGetAllLibrariesQuery = async <
  TData = Awaited<ReturnType<typeof getAllLibraries>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllLibraries>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllLibrariesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Libraries
 */
export const invalidateGetAllLibraries = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllLibrariesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Create Library
 */
export const createLibrary = (
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLibraryResponse>(
    {
      url: `/v2/data-harmonization/libraries`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLLibraryCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateLibraryQueryKey = (tLLibraryCreateRequest?: BodyType<TLLibraryCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'libraries', tLLibraryCreateRequest] as const;
};

export const getCreateLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateLibraryQueryKey(tLLibraryCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createLibrary>>> = ({ signal }) =>
    createLibrary(tLLibraryCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createLibrary>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof createLibrary>>>;
export type CreateLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateLibrary<
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createLibrary>>,
          TError,
          Awaited<ReturnType<typeof createLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateLibrary<
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createLibrary>>,
          TError,
          Awaited<ReturnType<typeof createLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateLibrary<
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Library
 */

export function useCreateLibrary<
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateLibraryQueryOptions(tLLibraryCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Library
 */
export const prefetchCreateLibraryQuery = async <
  TData = Awaited<ReturnType<typeof createLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateLibraryQueryOptions(tLLibraryCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Library
 */
export const invalidateCreateLibrary = async (
  queryClient: QueryClient,
  tLLibraryCreateRequest: BodyType<TLLibraryCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateLibraryQueryKey(tLLibraryCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Library
 */
export const deleteLibrary = (
  { libraryId }: DeleteLibraryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/libraries/${libraryId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteLibraryQueryKey = ({ libraryId }: DeleteLibraryPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'libraries', libraryId] as const;
};

export const getDeleteLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: DeleteLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteLibraryQueryKey({ libraryId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteLibrary>>> = ({ signal }) =>
    deleteLibrary({ libraryId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof deleteLibrary>>>;
export type DeleteLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteLibrary<
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteLibraryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteLibrary>>,
          TError,
          Awaited<ReturnType<typeof deleteLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteLibrary<
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteLibrary>>,
          TError,
          Awaited<ReturnType<typeof deleteLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteLibrary<
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Library
 */

export function useDeleteLibrary<
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: DeleteLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteLibraryQueryOptions({ libraryId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Library
 */
export const prefetchDeleteLibraryQuery = async <
  TData = Awaited<ReturnType<typeof deleteLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: DeleteLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteLibraryQueryOptions({ libraryId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Library
 */
export const invalidateDeleteLibrary = async (
  queryClient: QueryClient,
  { libraryId }: DeleteLibraryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteLibraryQueryKey({ libraryId }) }, options);

  return queryClient;
};

/**
 * @summary Get Library
 */
export const getLibrary = (
  { libraryId }: GetLibraryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLibraryResponse>(
    { url: `/v2/data-harmonization/libraries/${libraryId}`, method: 'GET', signal },
    options,
  );
};

export const getGetLibraryQueryKey = ({ libraryId }: GetLibraryPathParameters) => {
  return ['v2', 'data-harmonization', 'libraries', libraryId] as const;
};

export const getGetLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: GetLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLibraryQueryKey({ libraryId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLibrary>>> = ({ signal }) =>
    getLibrary({ libraryId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof getLibrary>>>;
export type GetLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useGetLibrary<
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLibrary>>,
          TError,
          Awaited<ReturnType<typeof getLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLibrary<
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLibrary>>,
          TError,
          Awaited<ReturnType<typeof getLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLibrary<
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Library
 */

export function useGetLibrary<
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: GetLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetLibraryQueryOptions({ libraryId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Library
 */
export const prefetchGetLibraryQuery = async <
  TData = Awaited<ReturnType<typeof getLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: GetLibraryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetLibraryQueryOptions({ libraryId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Library
 */
export const invalidateGetLibrary = async (
  queryClient: QueryClient,
  { libraryId }: GetLibraryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetLibraryQueryKey({ libraryId }) }, options);

  return queryClient;
};

/**
 * @summary Update Library
 */
export const updateLibrary = (
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLibraryResponse>(
    {
      url: `/v2/data-harmonization/libraries/${libraryId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLLibraryUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateLibraryQueryKey = (
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest?: BodyType<TLLibraryUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'libraries', libraryId, tLLibraryUpdateRequest] as const;
};

export const getUpdateLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateLibraryQueryKey({ libraryId }, tLLibraryUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateLibrary>>> = ({ signal }) =>
    updateLibrary({ libraryId }, tLLibraryUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof updateLibrary>>>;
export type UpdateLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateLibrary<
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateLibrary>>,
          TError,
          Awaited<ReturnType<typeof updateLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateLibrary<
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateLibrary>>,
          TError,
          Awaited<ReturnType<typeof updateLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateLibrary<
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Library
 */

export function useUpdateLibrary<
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateLibraryQueryOptions({ libraryId }, tLLibraryUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Library
 */
export const prefetchUpdateLibraryQuery = async <
  TData = Awaited<ReturnType<typeof updateLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateLibraryQueryOptions({ libraryId }, tLLibraryUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Library
 */
export const invalidateUpdateLibrary = async (
  queryClient: QueryClient,
  { libraryId }: UpdateLibraryPathParameters,
  tLLibraryUpdateRequest: BodyType<TLLibraryUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateLibraryQueryKey({ libraryId }, tLLibraryUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remove Data Checks From Library
 */
export const removeDataChecksFromLibrary = (
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataChecksRemoveResult>(
    {
      url: `/v2/data-harmonization/libraries/${libraryId}/data-checks`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataChecksRemoveRequest,
      signal,
    },
    options,
  );
};

export const getRemoveDataChecksFromLibraryQueryKey = (
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest?: BodyType<TLDataChecksRemoveRequest>,
) => {
  return [
    'DELETE',
    'v2',
    'data-harmonization',
    'libraries',
    libraryId,
    'data-checks',
    tLDataChecksRemoveRequest,
  ] as const;
};

export const getRemoveDataChecksFromLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRemoveDataChecksFromLibraryQueryKey({ libraryId }, tLDataChecksRemoveRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>> = ({ signal }) =>
    removeDataChecksFromLibrary({ libraryId }, tLDataChecksRemoveRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveDataChecksFromLibraryQueryResult = NonNullable<
  Awaited<ReturnType<typeof removeDataChecksFromLibrary>>
>;
export type RemoveDataChecksFromLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveDataChecksFromLibrary<
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
          TError,
          Awaited<ReturnType<typeof removeDataChecksFromLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveDataChecksFromLibrary<
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
          TError,
          Awaited<ReturnType<typeof removeDataChecksFromLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveDataChecksFromLibrary<
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Data Checks From Library
 */

export function useRemoveDataChecksFromLibrary<
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveDataChecksFromLibraryQueryOptions({ libraryId }, tLDataChecksRemoveRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Data Checks From Library
 */
export const prefetchRemoveDataChecksFromLibraryQuery = async <
  TData = Awaited<ReturnType<typeof removeDataChecksFromLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDataChecksFromLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveDataChecksFromLibraryQueryOptions({ libraryId }, tLDataChecksRemoveRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Data Checks From Library
 */
export const invalidateRemoveDataChecksFromLibrary = async (
  queryClient: QueryClient,
  { libraryId }: RemoveDataChecksFromLibraryPathParameters,
  tLDataChecksRemoveRequest: BodyType<TLDataChecksRemoveRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemoveDataChecksFromLibraryQueryKey({ libraryId }, tLDataChecksRemoveRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Library Data Checks
 */
export const getLibraryDataChecks = (
  { libraryId }: GetLibraryDataChecksPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataChecksResponse>(
    { url: `/v2/data-harmonization/libraries/${libraryId}/data-checks`, method: 'GET', signal },
    options,
  );
};

export const getGetLibraryDataChecksQueryKey = ({ libraryId }: GetLibraryDataChecksPathParameters) => {
  return ['v2', 'data-harmonization', 'libraries', libraryId, 'data-checks'] as const;
};

export const getGetLibraryDataChecksQueryOptions = <
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: GetLibraryDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLibraryDataChecksQueryKey({ libraryId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLibraryDataChecks>>> = ({ signal }) =>
    getLibraryDataChecks({ libraryId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetLibraryDataChecksQueryResult = NonNullable<Awaited<ReturnType<typeof getLibraryDataChecks>>>;
export type GetLibraryDataChecksQueryError = ErrorType<TLHTTPValidationError>;

export function useGetLibraryDataChecks<
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryDataChecksPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLibraryDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getLibraryDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLibraryDataChecks<
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLibraryDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getLibraryDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLibraryDataChecks<
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetLibraryDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Library Data Checks
 */

export function useGetLibraryDataChecks<
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: GetLibraryDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetLibraryDataChecksQueryOptions({ libraryId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Library Data Checks
 */
export const prefetchGetLibraryDataChecksQuery = async <
  TData = Awaited<ReturnType<typeof getLibraryDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: GetLibraryDataChecksPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLibraryDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetLibraryDataChecksQueryOptions({ libraryId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Library Data Checks
 */
export const invalidateGetLibraryDataChecks = async (
  queryClient: QueryClient,
  { libraryId }: GetLibraryDataChecksPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetLibraryDataChecksQueryKey({ libraryId }) }, options);

  return queryClient;
};

/**
 * @summary Add Data Checks To Library
 */
export const addDataChecksToLibrary = (
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataChecksCreateResult>(
    {
      url: `/v2/data-harmonization/libraries/${libraryId}/data-checks`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataCheckCreateRequest,
      signal,
    },
    options,
  );
};

export const getAddDataChecksToLibraryQueryKey = (
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest?: BodyType<TLDataCheckCreateRequest[]>,
) => {
  return ['POST', 'v2', 'data-harmonization', 'libraries', libraryId, 'data-checks', tLDataCheckCreateRequest] as const;
};

export const getAddDataChecksToLibraryQueryOptions = <
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddDataChecksToLibraryQueryKey({ libraryId }, tLDataCheckCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addDataChecksToLibrary>>> = ({ signal }) =>
    addDataChecksToLibrary({ libraryId }, tLDataCheckCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: libraryId !== null && libraryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddDataChecksToLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof addDataChecksToLibrary>>>;
export type AddDataChecksToLibraryQueryError = ErrorType<TLHTTPValidationError>;

export function useAddDataChecksToLibrary<
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDataChecksToLibrary>>,
          TError,
          Awaited<ReturnType<typeof addDataChecksToLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDataChecksToLibrary<
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDataChecksToLibrary>>,
          TError,
          Awaited<ReturnType<typeof addDataChecksToLibrary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDataChecksToLibrary<
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Data Checks To Library
 */

export function useAddDataChecksToLibrary<
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddDataChecksToLibraryQueryOptions({ libraryId }, tLDataCheckCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Data Checks To Library
 */
export const prefetchAddDataChecksToLibraryQuery = async <
  TData = Awaited<ReturnType<typeof addDataChecksToLibrary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDataChecksToLibrary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddDataChecksToLibraryQueryOptions({ libraryId }, tLDataCheckCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Data Checks To Library
 */
export const invalidateAddDataChecksToLibrary = async (
  queryClient: QueryClient,
  { libraryId }: AddDataChecksToLibraryPathParameters,
  tLDataCheckCreateRequest: BodyType<TLDataCheckCreateRequest[]>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddDataChecksToLibraryQueryKey({ libraryId }, tLDataCheckCreateRequest) },
    options,
  );

  return queryClient;
};
